/*
 * Jira Ticket:
 * Created Date: Wed, 11th Mar 2020, 15:50:31 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { Fragment, useEffect, useRef } from 'react';
import {
  Datagrid,
  FunctionField,
  NumberField,
  Pagination,
  ReferenceManyField,
  TextField,
} from 'react-admin';
import debounce from 'lodash.debounce';
import { Add, Edit } from '@material-ui/icons';
import SiteFilters from './filter';
import { LinkButton } from 'Components/Buttons';
import { tabbedLists } from 'styles';
import { isProjectManager } from 'utils/roles';
import setFilter from 'utils/tabbedFilter';
import { PostPagination } from '../../../../Components/CustomPagination/pagination';
// Note! The following error may appear in the console:
// "Total from response is not a number. Please check your dataProvider or the API."
// This is a known bug in react-admin: https://github.com/marmelab/react-admin/issues/4337

const SiteTab = ({
  className,
  component,
  formValues,
  dispatch,
  permissions,
  currentTabFilters,
  ...props
}) => {
  const classes = tabbedLists();
  const siteFilter = currentTabFilters && currentTabFilters.site;
  const {
    record: { id: organisationId },
  } = props;
  const canInvite = isProjectManager(permissions, props.id);

  const checkFilterValues = (currentFilter, filter) => {
    return (
      currentFilter !== filter.value ||
      currentFilter.name !== filter.value ||
      currentFilter.projectId !== filter.value
    );
  };

  const delayedSetFilter = useRef(
    debounce(
      filter =>
        setFilter({
          filter,
          setResourceFilter: filter =>
            dispatch({
              type: 'APP/SET_FILTER',
              tab: 'site',
              data: filter,
            }),
          checkFilterValues,
          currentFilter: siteFilter,
        }),
      750
    )
  ).current;

  const onFilterChange = (event, name = null) => {
    if (event.target) {
      return delayedSetFilter(event.target);
    } else if (!event.target && name) {
      // must reset the field manually as resettable click is not picked up in the event
      return dispatch({
        type: 'APP/SET_FILTER',
        tab: 'site',
        data: { [name]: null },
      });
    }
    return delayedSetFilter();
  };

  // clear the filter on unmount
  useEffect(() => {
    dispatch({
      type: 'APP/CLEAR_FILTERS',
      tab: 'site',
    });
  }, [dispatch]);

  return (
    <Fragment>
      <div className={classes.listFilterCreate}>
        <SiteFilters
          setFilters={onFilterChange}
          organisationId={organisationId}
        />
        {permissions && canInvite ? (
          <LinkButton
            label={'ra.action.create'}
            pathname={'/Site/create'}
            state={{
              organisationId,
            }}
            icon={<Add />}
          />
        ) : null}
      </div>
      <ReferenceManyField
        reference="Site"
        target="organisation"
        source="id"
        filter={siteFilter}
        pagination={<PostPagination />}
        bulkActionButtons={false}
        {...props}
      >
        <Datagrid
          className={classes.root}
          rowClick={(id, basePath, record) => {
            return isProjectManager(permissions, record.id)
              ? {
                  pathname: `/Site/${record.id}`,
                  state: {
                    organisationId,
                  },
                }
              : null;
          }}
        >
          <TextField
            source="name"
            label={'resources.Site.fields.name'}
            sortable={false}
          />
          <NumberField
            source="project.name"
            label={'resources.Site.fields.project'}
            sortable={false}
          />
          <NumberField
            source="statistics.numberOfTowers"
            label={'resources.Site.fields.numberOfTowers'}
            sortable={false}
          />
        </Datagrid>
      </ReferenceManyField>
    </Fragment>
  );
};

export default SiteTab;
